import isNil from "lodash/isNil";
import isString from "lodash/isString";
import toNumber from "lodash/toNumber";
import moment from "moment";
import invariant from "tiny-invariant";

import { LANGUAGE_ID, LANG_KEYS } from "../lang/lang.constants";

import LocalStorage from "./LocalStorage.service";

const DECIMAL_NUM = 3;

export default class Localization {
  static getSeparator(
    locale: LANGUAGE_ID,
    separatorType: "decimal" | "group",
  ): string {
    const num = 10000.1;
    const intl = Intl.NumberFormat(locale);

    // for safari
    if (!intl.formatToParts) {
      const separators = num.toLocaleString(locale).replace(/\d/g, "");

      if (separatorType === "decimal") {
        return separators[1] ?? ","; // fallback to comma if not successful
      }

      if (separatorType === "group" && separators[0]) {
        return separators[0] ?? " "; // fallback to space if not successful
      }
    }

    const parts = intl.formatToParts(num);
    const match = parts.find((part) => part.type === separatorType);

    invariant(
      match,
      `match was not found for separatorType: ${separatorType} in ${JSON.stringify(parts)}`,
    );

    return match.value;
  }

  static getDecimalSeparator(locale: LANGUAGE_ID): string {
    return Localization.getSeparator(locale, "decimal");
  }

  static getGroupSeparator(locale: LANGUAGE_ID): string {
    return Localization.getSeparator(locale, "group");
  }

  static num2str(
    val: number | string | undefined | null,
    locale: LANGUAGE_ID,
    formatDecimal = DECIMAL_NUM,
  ): string {
    return typeof val !== "string" && !isNil(val) && !isNaN(Number(val))
      ? Intl.NumberFormat(locale, {
          minimumFractionDigits: formatDecimal,
          maximumFractionDigits: formatDecimal,
        }).format(val)
      : "";
  }

  static num2strNonFixed(
    val: number | string,
    locale: LANGUAGE_ID,
    minDecimal = 0,
    maxDecimal = 10,
  ): string {
    if (typeof val === "string" && val.length === 0) {
      return "";
    }

    if (isNil(val) || isNaN(Number(val))) {
      return "";
    }

    return Intl.NumberFormat(locale, {
      minimumFractionDigits: minDecimal,
      maximumFractionDigits: maxDecimal,
    }).format(Number(val));
  }

  static transformStr(str: string, locale: LANGUAGE_ID): string {
    let groupRegExPattern = Localization.getGroupSeparator(locale);
    groupRegExPattern =
      groupRegExPattern.replace(/\s/g, "").length === 0
        ? "\\s"
        : groupRegExPattern;
    groupRegExPattern = groupRegExPattern === "." ? "\\." : groupRegExPattern;

    return str
      .replace(new RegExp(groupRegExPattern, "g"), "")
      .replace(Localization.getDecimalSeparator(locale), ".");
  }

  static localizeAndRoundDecimalString(
    value: string,
    locale: LANGUAGE_ID,
    formatDecimal = DECIMAL_NUM,
  ): string {
    if (typeof value !== "string") return value;

    // replace comma with a dot for correct parsing
    const num = Number(value.replace(",", "."));
    if (isNaN(num)) return value;

    // determine the length of the decimal part in the original string
    const [, decimalPart] = value.split(/[.,]/);
    let decimalLength = decimalPart ? decimalPart.length : 0;

    if (decimalLength > formatDecimal) {
      decimalLength = formatDecimal;
    }

    // if there is no decimal part, return without formating
    if (decimalLength === 0) {
      return num.toString();
    }

    // formatting for locale and rounding
    return num.toLocaleString(locale, {
      minimumFractionDigits: decimalLength,
      maximumFractionDigits: decimalLength,
    });
  }

  static transformStr2Num(str: string, locale: LANGUAGE_ID): number {
    const newStr = this.transformStr(str, locale);
    return toNumber(newStr);
  }

  static checkValidStr(str: string | undefined, locale: LANGUAGE_ID): boolean {
    if (str === undefined) return false;
    const newStr = this.transformStr(str, locale);
    const isLastCharNonNumeric = /\D$/.test(newStr);
    const hasValidNumberFormat = /^[+-]?\d+(\.\d+)?$/.test(newStr);

    return !isLastCharNonNumeric && hasValidNumberFormat;
  }

  static str2num(
    val: number | string | null | undefined,
    locale: LANGUAGE_ID,
    formatDecimal = DECIMAL_NUM,
  ): number | string {
    if (val === null) {
      return "";
    }

    if (val === undefined) {
      return "";
    }

    if (typeof val === "number") {
      return toNumber(val.toFixed(formatDecimal));
    }

    const str = val ? val.replace(/ /g, "") : val;

    if (!str || !isString(str)) {
      return "";
    }

    const result = this.transformStr2Num(str, locale);

    return isNaN(Number(result))
      ? val
      : toNumber(result.toFixed(formatDecimal));
  }

  static str2numNonFixed(
    val: number | string,
    locale: LANGUAGE_ID,
  ): number | string {
    if (typeof val === "number") {
      return val;
    }

    const str = val ? val.replace(/ /g, "") : val;

    if (!str || !isString(str)) {
      return "";
    }

    const result = this.transformStr2Num(str, locale);

    return isNaN(Number(result)) ? val : result;
  }

  static formatDate(
    date: Date,
    locale: LANGUAGE_ID,
    opts: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "numeric",
    },
  ): string {
    return Intl.DateTimeFormat(locale, opts).format(date);
  }

  static formatTime(date: Date, locale: LANGUAGE_ID): string {
    return Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "numeric",
    }).format(date);
  }

  static mergeTranslations = (
    ...sources: Record<LANGUAGE_ID, Record<string, string>>[]
  ) => {
    const res = {} as Record<LANGUAGE_ID, Record<string, string>>;
    (Object.values(LANG_KEYS) as LANGUAGE_ID[]).forEach((langId) => {
      res[langId] = {};
      sources.forEach((src) => {
        res[langId] = {
          ...res[langId],
          ...src[langId],
        };
      });
    });

    return res;
  };

  static parseNumberFromLocalizedString = (numberString: string) => {
    // Remove all spaces
    const cleanedNumberString = numberString.replace(/\s/g, "");

    // Check if the cleaned string contains any delimiters
    if (
      cleanedNumberString.includes(".") ||
      cleanedNumberString.includes(",")
    ) {
      // Identify the last dot and comma
      const lastDotIndex = cleanedNumberString.lastIndexOf(".");
      const lastCommaIndex = cleanedNumberString.lastIndexOf(",");

      let normalizedNumberString;
      if (lastCommaIndex > lastDotIndex) {
        // Last comma is the decimal separator
        normalizedNumberString = cleanedNumberString
          .replace(/\./g, "")
          .replace(/,/g, ".");
      } else if (lastDotIndex > lastCommaIndex) {
        // Last dot is the decimal separator
        normalizedNumberString = cleanedNumberString.replace(/,/g, "");
      } else {
        // No decimal separator
        normalizedNumberString = cleanedNumberString
          .replace(/,/g, "")
          .replace(/\./g, "");
      }

      return parseFloat(normalizedNumberString);
    } else {
      // If there are no delimiters, simply parse the cleaned string as a float
      return parseFloat(cleanedNumberString);
    }
  };

  static initLangInLocalStorage = () => {
    const currentLangId = LocalStorage.loadFromLocalStorage<string>("lang");

    if (!currentLangId || !Object.hasOwn(LANGUAGES, currentLangId)) {
      const userLanguage = navigator.language.split("-")[0];
      const supportedLanguage = Object.entries(LANGUAGES).find(([, language]) =>
        language.ISO_CODE.startsWith(userLanguage),
      )?.[0];

      LocalStorage.saveToLocalStorage(supportedLanguage ?? "en-US", "lang");
    }
  };

  static setLang = (langId: string) => {
    LocalStorage.saveToLocalStorage(langId, "lang");
    moment.locale(langId);
    window.location.reload();
  };

  static getLang = (): LANGUAGE_ID => {
    Localization.initLangInLocalStorage();

    // after calling initLangInLocalStorage, we can be sure that lang is set in local storage
    return LocalStorage.loadFromLocalStorage<LANGUAGE_ID>("lang")!;
  };

  static readonly getLangShortCode = (): string => {
    const langLongCode = LocalStorage.loadFromLocalStorage<LANGUAGE_ID>("lang");

    return langLongCode?.slice(-2) ?? "";
  };
}

export const LANGUAGES = {
  "cs-CZ": {
    NAME: "Česky",
    ISO_CODE: "cs",
  },
  "en-US": {
    NAME: "English",
    ISO_CODE: "en",
  },
  "es-ES": {
    NAME: "Español",
    ISO_CODE: "es",
  },
  "ro-RO": {
    NAME: "Romanesc",
    ISO_CODE: "ro",
  },
  "hu-HU": {
    NAME: "Magyar",
    ISO_CODE: "hu",
  },
  "pl-PL": {
    NAME: "Polski",
    ISO_CODE: "pl",
  },
  "sk-SK": {
    NAME: "Slovensky",
    ISO_CODE: "sk",
  },
  "lt-LT": {
    NAME: "Lietuvių",
    ISO_CODE: "lt",
  },
};
